/**
 * LAUNCH FLAGS — features hidden for launch because they're incomplete or
 * broken. Flip a flag to `true` once the feature is actually finished.
 *
 * These exist so half-built surfaces don't mislead/confuse users (e.g. a
 * mission list you can never complete). Each is tracked in memory
 * (project_deferred_features) with what's missing. Found in the 2026-06 audit.
 */
export const LAUNCH_FLAGS = {
  // completeMission() has zero callers → weekly missions can never be completed
  // (always 0/3). Wire completion + reward before enabling. Rendered in
  // WeeklyMissionsCard + OperatorIdentityPanel.
  weeklyMissions: false,

  // Fitbit Cloud Functions are commented out (functions/src/index.ts) and
  // NEXT_PUBLIC_FITBIT_CLIENT_ID is unset → the connect flow is a dead end.
  fitbit: false,

  /**
   * TensorFlow.js models — OFF.
   *
   * The load-recommendation model has never produced a single recommendation
   * for any user, and provably could not: its confidence is
   * `min(0.95, 0.6 + (history.length / 50) * 0.35)` (mlLoadRecommendation.ts:347)
   * while history is hard-capped at 10 entries (`slice(-10)`, :516). Maximum
   * achievable confidence is therefore 0.67, and the only gate that consumes it
   * requires >= 0.7 (unifiedRecommendation.ts:454). Unreachable by arithmetic.
   *
   * Meanwhile dataLoader.ts kicked off a 100-epoch training run in the
   * background on app start for anyone with 20+ sessions — real battery and CPU
   * spent training a model whose output is discarded.
   *
   * Turning this off costs nothing (no user has ever seen its output) and stops
   * the waste. The statistical recommender, which does all the real work, is
   * unaffected. Re-enable only alongside a fix to the confidence ceiling AND
   * evidence the model beats the statistical path on held-out data.
   */
  tensorflowModels: false,

  // "Share / Submit for review" writes to programSubmissions but there's no
  // user-facing review/approval pipeline → looks half-built.
  programSharing: false,

  // Community/team deployments: join-by-code works but there's no create-team
  // UI and the team view is never mounted with a deploymentId.
  communityTeams: false,
} as const;
